import React, { useState, useEffect } from 'react';
import { app } from './firebase';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';

const db = getFirestore(app);

export default function SettingsView({ logAction, employeeProfile }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  
  // Timeout State
  const [loginTimeout, setLoginTimeout] = useState(4);
  
  // Personal Color State
  const [profileColor, setProfileColor] = useState('#0d6efd'); // Default Blue

  // Theme State
  const [theme, setTheme] = useState('dark');

  useEffect(() => {
      // Load saved settings
      if (employeeProfile) {
          if (employeeProfile.settings && employeeProfile.settings.loginTimeout) {
              setLoginTimeout(employeeProfile.settings.loginTimeout);
          }
          if (employeeProfile.settings && employeeProfile.settings.theme) {
              setTheme(employeeProfile.settings.theme);
          }
          if (employeeProfile.profileColor) {
              setProfileColor(employeeProfile.profileColor);
          }
      }
  }, [employeeProfile]);

  const handlePasswordChange = (e) => {
    e.preventDefault();
    if(password !== confirm) return alert("Passwords do not match");
    alert("Password change requires re-authentication (Not implemented in this demo view).");
  };

  const handleSavePreferences = async () => {
      if (!employeeProfile || !employeeProfile.id) return alert("Error: No employee profile found.");
      
      try {
          const empRef = doc(db, "employees", employeeProfile.id);
          await updateDoc(empRef, {
              "settings.loginTimeout": parseFloat(loginTimeout),
              "settings.theme": theme,
              "profileColor": profileColor
          });
          logAction("Update Settings", `Updated preferences for ${employeeProfile.email}`);
          alert("Preferences saved successfully.");
      } catch (err) {
          console.error(err);
          alert("Failed to save settings.");
      }
  };

  return (
    <div className="container-fluid p-0">
      <div className="mb-4">
        <h2 className="fw-bold text-main">My Settings</h2>
        <p className="text-muted-theme">Manage your personal preferences and security.</p>
      </div>

      <div className="row g-4">
        {/* PROFILE CARD */}
        <div className="col-md-6">
          <div className="card h-100 bg-card border-theme shadow-sm">
            <div className="card-header bg-transparent border-theme">
              <h5 className="mb-0 text-main">Profile Information</h5>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <label className="form-label text-muted-theme small">Email Address</label>
                <input type="text" className="form-control" value={employeeProfile?.email || ''} disabled readOnly />
              </div>
              <div className="mb-3">
                <label className="form-label text-muted-theme small">Role</label>
                <input type="text" className="form-control" value={employeeProfile?.accessRole || 'Visitor'} disabled readOnly />
              </div>
            </div>
          </div>
        </div>

        {/* APP PREFERENCES */}
        <div className="col-md-6">
          <div className="card h-100 bg-card border-theme shadow-sm">
            <div className="card-header bg-transparent border-theme">
              <h5 className="mb-0 text-main">Application Preferences</h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                  <div className="col-6">
                      <label className="form-label text-main small fw-bold">Theme</label>
                      <select 
                          className="form-select" 
                          value={theme} 
                          onChange={(e) => setTheme(e.target.value)}
                      >
                          <option value="dark">Dark (Default)</option>
                          <option value="light">Light</option>
                          <option value="oled">OLED (Black)</option>
                          <option value="dracula">Dracula</option>
                          <option value="neon">Neon (Vibrant)</option>
                      </select>
                  </div>
                  <div className="col-6">
                      <label className="form-label text-main small fw-bold">Session Timeout</label>
                      <select 
                          className="form-select" 
                          value={loginTimeout} 
                          onChange={(e) => setLoginTimeout(e.target.value)}
                      >
                          <option value="0.5">30 Mins</option>
                          <option value="1">1 Hour</option>
                          <option value="4">4 Hours</option>
                          <option value="8">8 Hours</option>
                      </select>
                  </div>
              </div>

              <div className="mt-4 mb-3">
                  <label className="form-label text-main small fw-bold">My Kanban Color</label>
                  <div className="d-flex align-items-center gap-3">
                      <input 
                          type="color" 
                          className="form-control form-control-color" 
                          value={profileColor} 
                          onChange={(e) => setProfileColor(e.target.value)} 
                          title="Choose your color"
                      />
                      <div className="p-2 border border-theme rounded bg-input text-main small flex-grow-1">
                          Preview: <span className="badge" style={{backgroundColor: profileColor}}>Your Tasks</span>
                      </div>
                  </div>
              </div>

              <div className="d-flex justify-content-end mt-4">
                  <button className="btn btn-primary" onClick={handleSavePreferences}>Save Preferences</button>
              </div>
            </div>
          </div>
        </div>

        {/* SECURITY */}
        <div className="col-md-12">
          <div className="card bg-card border-theme shadow-sm">
            <div className="card-header bg-transparent border-theme">
              <h5 className="mb-0 text-main">Security</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handlePasswordChange}>
                <div className="row g-3 align-items-end">
                  <div className="col-md-4">
                    <label className="form-label text-muted-theme small">New Password</label>
                    <input type="password" className="form-control" value={password} onChange={e => setPassword(e.target.value)} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label text-muted-theme small">Confirm Password</label>
                    <input type="password" className="form-control" value={confirm} onChange={e => setConfirm(e.target.value)} />
                  </div>
                  <div className="col-md-4">
                    <button className="btn btn-outline-danger w-100">Update Password</button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}